import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Pagination } from '../../components/common/Pagination';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { useWorkOrdersPage } from './hooks/useWorkOrdersPage';
import { WorkOrdersHeader } from './components/WorkOrdersHeader';
import { WorkOrdersFilters } from './components/WorkOrdersFilters';
import { WorkOrdersTable } from './components/WorkOrdersTable';
import { WorkOrdersCards } from './components/WorkOrdersCards';
import { WorkOrdersModeToggle } from './components/WorkOrdersModeToggle';
import { FgProductionsPanel } from './components/FgProductionsPanel';
import { EditWorkOrderModal } from './components/EditWorkOrderModal';
import { MoveToInventoryModal } from './components/MoveToInventoryModal';
import { WorkOrderConfirmModal } from './components/WorkOrderConfirmModal';
import { RoastQuotaExceededModal } from '../../components/subscription/RoastQuotaExceededModal';
import type { SubscriptionTier } from '../../services/subscriptionService';
import type { WorkOrder } from '../../types/workOrder';
import type { FinishedGoodsProductionModalMode } from '../../hooks/inventory-hooks/useFinishedGoodsProductionForm';

export const WorkOrdersPage: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  // Spec 100: work_orders was split into .roasting (this page) and .packing
  // (the FG productions panel below). Each half has its own grants; the
  // mode-aware variables below feed the right one to children based on which
  // tab is active.
  const canReadRoasting = can('work_orders.roasting', 'read');
  const canReadPacking = can('work_orders.packing', 'read');
  const canWriteRoasting = can('work_orders.roasting', 'write');
  const canWritePacking = can('work_orders.packing', 'write');
  const canDeleteRoasting = can('work_orders.roasting', 'delete');
  const canDeletePacking = can('work_orders.packing', 'delete');
  const [searchParams, setSearchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  // If user only has access to one half, force-select that half.
  const initialMode: 'roasting' | 'finished_good' = (() => {
    if (modeParam === 'finished_good' && canReadPacking) return 'finished_good';
    if (modeParam === 'roasting' && canReadRoasting) return 'roasting';
    if (canReadRoasting) return 'roasting';
    if (canReadPacking) return 'finished_good';
    return 'roasting'; // unreachable when route guard is in place
  })();
  const [mode, setMode] = React.useState<'roasting' | 'finished_good'>(initialMode);
  const canWrite = mode === 'roasting' ? canWriteRoasting : canWritePacking;
  const canDelete = mode === 'roasting' ? canDeleteRoasting : canDeletePacking;

  const [fgProductionOpen, setFgProductionOpen] = React.useState(false);
  const [fgProductionMode, setFgProductionMode] = React.useState<FinishedGoodsProductionModalMode>('create_draft');
  const [fgProductionId, setFgProductionId] = React.useState<string | undefined>(undefined);

  const [isCancelWorkOrderModalOpen, setIsCancelWorkOrderModalOpen] = React.useState(false);
  const [cancelWorkOrderTarget, setCancelWorkOrderTarget] = React.useState<WorkOrder | null>(null);
  const [cancelWorkOrderSubmitting, setCancelWorkOrderSubmitting] = React.useState(false);
  const [cancelWorkOrderError, setCancelWorkOrderError] = React.useState<string | null>(null);
  const [isDeleteWorkOrderModalOpen, setIsDeleteWorkOrderModalOpen] = React.useState(false);
  const [deleteWorkOrderTarget, setDeleteWorkOrderTarget] = React.useState<WorkOrder | null>(null);
  const [deleteWorkOrderSubmitting, setDeleteWorkOrderSubmitting] = React.useState(false);
  const [deleteWorkOrderError, setDeleteWorkOrderError] = React.useState<string | null>(null);

  const {
    loading,
    orders,
    inventoryItems,
    editingOrder,
    setEditingOrder,
    isFinishedGoodsModalOpen,
    closeFinishedGoodsModal,
    finishedGoodInitialData,
    selectedWorkOrder,
    finishedGoodMode,
    handleEditOrder,
    handleUpdateOrder,
    handleStatusChange,
    handleCancelOrder,
    handleDeleteOrder,
    handleCompleteFromCard,
    handleMoveToInventory,
    handleCreateFinishedGood,
    statusOptions,
    roastTypeLabelMap,
    roastTypeOptions,
    searchFilter,
    setSearchFilter,
    statusFilter,
    setStatusFilter,
    roastTypeFilter,
    setRoastTypeFilter,
    machineFilter,
    setMachineFilter,
    machineTypeFilter,
    setMachineTypeFilter,
    machineOptions,
    machineTypeOptions,
    filteredOrders,
    totalOrders,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortConfig,
    handleSort,
    shouldGroup,
    groupedOrders,
    sortedDates,
    formatDate,
    formatNumber,
    formatPercent,
    handleCreateWorkOrder,
    quotaError,
    clearQuotaError,
  } = useWorkOrdersPage();

  const isEditingLocked =
    editingOrder?.status === 'cancelled' ||
    (editingOrder?.status === 'completed' && Boolean(editingOrder?.isSavedAsRecipe));

  const handleModeChange = (next: 'roasting' | 'finished_good') => {
    setMode(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'roasting') nextParams.delete('mode');
    else nextParams.set('mode', 'finished_good');
    setSearchParams(nextParams, { replace: true });
  };

  const openProduction = (nextMode: FinishedGoodsProductionModalMode, id?: string) => {
    setFgProductionMode(nextMode);
    setFgProductionId(id);
    setFgProductionOpen(true);
  };

  const closeProduction = () => {
    setFgProductionOpen(false);
    setFgProductionId(undefined);
    setFgProductionMode('create_draft');
  };

  const openCancelWorkOrderModal = (order: WorkOrder) => {
    setCancelWorkOrderError(null);
    setCancelWorkOrderTarget(order);
    setIsCancelWorkOrderModalOpen(true);
  };

  const closeCancelWorkOrderModal = () => {
    if (cancelWorkOrderSubmitting) return;
    setIsCancelWorkOrderModalOpen(false);
    setCancelWorkOrderTarget(null);
    setCancelWorkOrderError(null);
  };

  const submitCancelWorkOrder = async () => {
    if (!cancelWorkOrderTarget) {
      closeCancelWorkOrderModal();
      return;
    }
    setCancelWorkOrderSubmitting(true);
    try {
      await handleCancelOrder(cancelWorkOrderTarget.id);
      setIsCancelWorkOrderModalOpen(false);
      const closedTargetId = cancelWorkOrderTarget.id;
      setCancelWorkOrderTarget(null);
      if (editingOrder?.id === closedTargetId) setEditingOrder(null);
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        t('workOrders.page.errorCancel');
      setCancelWorkOrderError(message);
    } finally {
      setCancelWorkOrderSubmitting(false);
    }
  };

  const openDeleteWorkOrderModal = (order: WorkOrder) => {
    setDeleteWorkOrderError(null);
    setDeleteWorkOrderTarget(order);
    setIsDeleteWorkOrderModalOpen(true);
  };

  const closeDeleteWorkOrderModal = () => {
    if (deleteWorkOrderSubmitting) return;
    setIsDeleteWorkOrderModalOpen(false);
    setDeleteWorkOrderTarget(null);
    setDeleteWorkOrderError(null);
  };

  const submitDeleteWorkOrder = async () => {
    if (!deleteWorkOrderTarget) {
      closeDeleteWorkOrderModal();
      return;
    }
    setDeleteWorkOrderSubmitting(true);
    setDeleteWorkOrderError(null);
    try {
      await handleDeleteOrder(deleteWorkOrderTarget);
      setIsDeleteWorkOrderModalOpen(false);
      const closedTargetId = deleteWorkOrderTarget.id;
      setDeleteWorkOrderTarget(null);
      if (editingOrder?.id === closedTargetId) setEditingOrder(null);
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        t('workOrders.page.errorDelete');
      setDeleteWorkOrderError(message);
    } finally {
      setDeleteWorkOrderSubmitting(false);
    }
  };

  if (loading && orders.length === 0) {
    return <LoadingSpinner text={t('workOrders.page.loading')} />;
  }

  return (
    <div className="space-y-6" data-test-id="work-orders-page">
      <WorkOrdersHeader
        label={mode === 'roasting' ? t('workOrders.page.newWorkOrder') : t('workOrders.page.packingProcess')}
        onAction={mode === 'roasting' ? handleCreateWorkOrder : () => openProduction('create_draft')}
        canWrite={canWrite}
      />

      <WorkOrdersModeToggle
        mode={mode}
        onChange={handleModeChange}
        canShowRoasting={canReadRoasting}
        canShowPacking={canReadPacking}
      />

      {mode === 'roasting' && (
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <WorkOrdersFilters
            searchFilter={searchFilter}
            setSearchFilter={setSearchFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            roastTypeFilter={roastTypeFilter}
            setRoastTypeFilter={setRoastTypeFilter}
            machineFilter={machineFilter}
            setMachineFilter={setMachineFilter}
            machineTypeFilter={machineTypeFilter}
            setMachineTypeFilter={setMachineTypeFilter}
            statusOptions={statusOptions}
            roastTypeOptions={roastTypeOptions}
            roastTypeLabelMap={roastTypeLabelMap}
            machineOptions={machineOptions}
            machineTypeOptions={machineTypeOptions}
          />

          <div className="bg-white shadow sm:rounded-lg border border-gray-200">
            {filteredOrders.length === 0 ? (
              <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
                {t('workOrders.page.emptyFiltered')}
              </div>
            ) : (
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                <WorkOrdersTable
                  orders={filteredOrders}
                  shouldGroup={shouldGroup}
                  groupedOrders={groupedOrders}
                  sortedDates={sortedDates}
                  sortConfig={sortConfig}
                  handleSort={handleSort}
                  roastTypeLabelMap={roastTypeLabelMap}
                  formatDate={formatDate}
                  formatNumber={formatNumber}
                  formatPercent={formatPercent}
                  onEditOrder={handleEditOrder}
                  onStatusChange={handleStatusChange}
                  onCancelOrder={openCancelWorkOrderModal}
                  onDeleteOrder={openDeleteWorkOrderModal}
                  onMoveToInventory={handleMoveToInventory}
                  inventoryItems={inventoryItems}
                  canWrite={canWrite}
                  canDelete={canDelete}
                />
                <WorkOrdersCards
                  orders={filteredOrders}
                  shouldGroup={shouldGroup}
                  groupedOrders={groupedOrders}
                  sortedDates={sortedDates}
                  roastTypeLabelMap={roastTypeLabelMap}
                  formatDate={formatDate}
                  formatNumber={formatNumber}
                  formatPercent={formatPercent}
                  onEditOrder={handleEditOrder}
                  onCompleteOrder={handleCompleteFromCard}
                  onCancelOrder={openCancelWorkOrderModal}
                  onDeleteOrder={openDeleteWorkOrderModal}
                  onMoveToInventory={handleMoveToInventory}
                  inventoryItems={inventoryItems}
                  canWrite={canWrite}
                  canDelete={canDelete}
                />
              </div>
            )}
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalOrders}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        </div>
      )}

      {mode === 'finished_good' && (
        <FgProductionsPanel
          enabled={mode === 'finished_good'}
          productionOpen={fgProductionOpen}
          productionMode={fgProductionMode}
          productionId={fgProductionId}
          onCloseProduction={closeProduction}
          onOpenProduction={openProduction}
          roastTypeLabelMap={roastTypeLabelMap}
          canWrite={canWrite}
        />
      )}

      {mode === 'roasting' && (
        <>
          <WorkOrderConfirmModal
            isOpen={isCancelWorkOrderModalOpen}
            onClose={closeCancelWorkOrderModal}
            title={t('workOrders.cancelModal.title')}
            description={
              cancelWorkOrderTarget?.status === 'scheduled'
                ? t('workOrders.cancelModal.scheduledDescription')
                : t('workOrders.cancelModal.defaultDescription')
            }
            cancelLabel={t('common.actions.back', undefined, 'Back')}
            confirmLabel={t('workOrders.cancelModal.confirm')}
            confirmingLabel={t('workOrders.common.cancelling')}
            submitting={cancelWorkOrderSubmitting}
            errorMessage={cancelWorkOrderError}
            onConfirm={submitCancelWorkOrder}
            testId="wo-cancel-confirm-modal"
            cancelTestId="wo-cancel-confirm-close"
            confirmTestId="wo-cancel-confirm-submit"
            errorTestId="wo-cancel-confirm-error"
          />

          <WorkOrderConfirmModal
            isOpen={isDeleteWorkOrderModalOpen}
            onClose={closeDeleteWorkOrderModal}
            title={t('workOrders.deleteModal.title')}
            description={t('workOrders.deleteModal.description')}
            iconTone="red"
            confirmLabel={t('workOrders.deleteModal.confirm')}
            confirmingLabel={t('workOrders.common.deleting')}
            submitting={deleteWorkOrderSubmitting}
            errorMessage={deleteWorkOrderError}
            onConfirm={submitDeleteWorkOrder}
            testId="wo-delete-confirm-modal"
            cancelTestId="wo-delete-confirm-close"
            confirmTestId="wo-delete-confirm-submit"
            errorTestId="wo-delete-confirm-error"
          />

          <EditWorkOrderModal
            editingOrder={editingOrder}
            isEditingLocked={isEditingLocked}
            onClose={() => setEditingOrder(null)}
            onSubmit={handleUpdateOrder}
            canWrite={canWrite}
          />

          <MoveToInventoryModal
            isOpen={isFinishedGoodsModalOpen}
            onClose={closeFinishedGoodsModal}
            selectedWorkOrder={selectedWorkOrder}
            finishedGoodInitialData={finishedGoodInitialData}
            finishedGoodMode={finishedGoodMode}
            onCreateFinishedGood={handleCreateFinishedGood}
            canWrite={canWrite}
          />
        </>
      )}

      {quotaError && (
        <RoastQuotaExceededModal
          isOpen
          onClose={clearQuotaError}
          tier={quotaError.tier as SubscriptionTier}
          limitKg={quotaError.limit_kg}
          usedKg={quotaError.used_kg}
          requestedKg={quotaError.requested_kg}
          cycleEnd={new Date(quotaError.cycle_end)}
        />
      )}
    </div>
  );
};
